import axiosInstance from "../api/axios";
import useNotification from "../hooks/useNotification";
import { varBayerID, varExtProviderID } from "../utils/constants";
import {
  removeArrayObjectDuplicates,
  replaceFalseyValuesWithEmptyString,
  validProperties,
} from "../utils/utils";
import useDataPrivacy from "./useDataPrivacy";
import useProfile from "./useProfile";

const useCollect = () => {
  const { getDataPrivacy } = useDataPrivacy();
  const { profile, tokenData } = useProfile();
  const documentDomain = import.meta.env.VITE_DOMAIN;
  const { successToast, errorToast } = useNotification();
  const isDiscoverApi = (url) => url.includes("/discover");
  const getSenderName = () => {
    const firstName = tokenData?.given_name || profile?.givenName || "";
    const lastName = tokenData?.family_name || profile?.surname || "";

    return `${firstName} ${lastName}`.trim();
  };
  const priorityOrder = [
    "bayer_id",
    "vault_id",
    "veeva",
    "balios",
    "external_provider_id",
    "email",
  ];
  const sortByPriority = (arr) => {
    return arr.sort(
      (a, b) => priorityOrder.indexOf(a.type) - priorityOrder.indexOf(b.type)
    );
  };

  const postDiscoveryIdentity = async ({
    controller_code,
    customer_scope,
    type,
    value,
    external_provider_id,
    bayer_id,
    ...rest
  }) => {
    try {
      const list = [
        {
          type: type,
          value: value,
        },
      ];
      if (bayer_id)
        list.push({
          type: "bayer_id",
          value: bayer_id,
        });

      if (external_provider_id)
        list.push({
          type: "external_provider_id",
          value: external_provider_id,
        });
      rest?.sub_identities?.forEach((item) => {
        list.push({
          type: item.type,
          value: item.value,
        });
      });
      const body = {
        controller_code,
        customer_scope,
        list: sortByPriority(list),
      };
      return axiosInstance.post("/discover", body);
    } catch (e) {}
  };
  const stripUnwantedParams = (params) => {
    const copy = { ...params };
    delete copy?.[varBayerID];
    delete copy?.[varExtProviderID];
    delete copy?.collection_channel;
    delete copy?.last_update_channel;
    return copy;
  };
  const postCollect = async (
    mainIdentity,
    deletedParam,
    docValues,
    isConsent,
    passEmpty,
    senderNameConfig,
  ) => {
    if (mainIdentity.hasSubmited) {
      delete mainIdentity.hasSubmited;
    }
    let dpa_records = [];
    if (mainIdentity?.data_processing_authorization_records?.length > 0) {
      dpa_records = [
        ...(mainIdentity.data_processing_authorization_records || []),
      ];
      delete mainIdentity.data_processing_authorization_records;
    }
    let signature_file = "";
    if (mainIdentity?.signature_file) {
      signature_file = mainIdentity.signature_file;
      delete mainIdentity.signature_file;
    }

    const withEmpty = replaceFalseyValuesWithEmptyString(mainIdentity);
    const collect_activity = {
      collection_channel: withEmpty.collection_channel,
      last_update_channel: withEmpty.last_update_channel,
    };
    const collect_req_body = {
      digital_identity: stripUnwantedParams(
        passEmpty ? withEmpty : validProperties(withEmpty),
      ),
      collect_activity,
      ...(docValues
        ? {
            data_privacy_statement: {
              commit_hash: docValues.hash,
              statement_url: `${documentDomain}/${mainIdentity?.controller_code}/${mainIdentity?.customer_scope}/${mainIdentity.language}/ps/data_privacy_fragment/`,
              commit_datetime: docValues.dataTime,
              document_version_number: docValues.version,
            },
          }
        : {}),
      ...(deletedParam || {}),
    };
    if (isConsent)
      collect_req_body.remote_collect = {
        validity: 14,
        collect_url: `${documentDomain}/${mainIdentity?.controller_code}/${mainIdentity?.customer_scope}/${mainIdentity.language}/cs/consent_remote_collect`,
      };

    if (senderNameConfig?.requiredSenderNameInPayload === "1") {
      const senderName = getSenderName();

      if (senderName) {
        collect_req_body.digital_identity.sender_name = senderName;
      }
    }
    if (signature_file) {
      collect_req_body.signature_file = signature_file;
    }
    if (dpa_records?.length > 0) {
      collect_req_body.data_processing_authorization_records = dpa_records;
    }
    if (docValues && signature_file) {
      collect_req_body.consent_statement = {
        commit_hash: docValues.hash,
        statement_url: `${documentDomain}/${mainIdentity?.controller_code}/${mainIdentity?.customer_scope}/${mainIdentity.language}/ps/data_privacy_fragment/`,
        commit_datetime: docValues.dataTime,
        document_version_number: docValues.version,
      };
    }
    return axiosInstance.post("/v2/collect", { ...collect_req_body });
  };
  const customThrowErrorMessage = (message) => {
    throw {
      data: {
        message,
      },
    };
  };
  const checkBusinessValidation = (identityChecks) => {
    const businessCheckTypes = [
      "bayer_id",
      "veeva",
      "external_provider_id",
      "balios",
      "vault_id",
    ];
    if (identityChecks?.length > 0) {
      const alreadyExisting = identityChecks?.filter(
        (item) =>
          businessCheckTypes?.includes(item?.type) &&
          item.identity == "not_existent" &&
          item.type_present_as_sub_identity == "true"
      );
      if (alreadyExisting?.length > 0) {
        customThrowErrorMessage(
          "Master Data Management Error. Please Contact Back Office."
        );
      }
    }
  };

  const getSubIdValues = (identity_check) => {
    const subIdentities = identity_check?.reduce(
      (acc, item) => {
        if (
          item.identity === "not_existent" &&
          item.type_present_as_sub_identity === "false"
        ) {
          acc.newSubIds.push({ type: item.type, value: item.value });
        } else if (item.identity === "not_existent" && item.type === "email") {
          acc.emailSubIds.push({ type: item.type, value: item.value });
        } else if (item.identity === "other") {
          acc.otherSubIds.push({ type: item.type, value: item.value });
        }
        return acc;
      },
      { newSubIds: [], emailSubIds: [], otherSubIds: [] }
    );
    return [
      ...subIdentities.newSubIds,
      ...subIdentities.emailSubIds,
      ...subIdentities.otherSubIds,
    ];
  };

  const validateDiscoverResponse = (response, param) => {
    const identity_check = response?.data?.digital_identity?.identity_check;
    checkBusinessValidation(identity_check);
    if (identity_check?.length > 0) {
      const subIds = getSubIdValues(identity_check);
      param.sub_identities = removeArrayObjectDuplicates(
        [...(subIds || [])],
        ["type", "value"]
      );
    }
    if (!param?.sub_identities?.length) delete param.sub_identities;
    return param;
  };

  const identityCollect = async (
    param,
    isCreate = false,
    isConsent = false,
    deletedParam = {},
    isrequiredPrivacy = true,
    validateDiscoverResp = false,
    passEmpty = true,
    senderNameConfig,
  ) => {
    let privacyDetails;
    try {
      privacyDetails = isrequiredPrivacy ? await getDataPrivacy(param) : false;
      const discoveryRes = await postDiscoveryIdentity(param);

      if (discoveryRes?.status == 200) {
        let newParam = "";
        if (validateDiscoverResp === true)
          newParam = validateDiscoverResponse(discoveryRes, param);
        else newParam = param;

        const collectRes = await postCollect(
          {
            ...newParam,
            ...(discoveryRes?.data?.digital_identity?.main_identity || {}),
          },
          deletedParam || {},
          privacyDetails,
          isConsent,
          passEmpty,
          senderNameConfig,
        );
        return collectRes;
      }
    } catch (error) {
      if (isDiscoverApi(error?.request?.responseURL || "") && isCreate) {
        if (error?.status == 400) {
          // Logic change: if discover API returns 400 and param.bayer_id is present
          if (!param.sub_identities) param.sub_identities = [];
          if (param.bayer_id) {
            // Add { type: param.type, value: param.value } to param.sub_identities (create array if needed)
            param.sub_identities.push({ type: param.type, value: param.value });
            // Set param.type = "bayer_id" and param.value = param.bayer_id
            param.type = "bayer_id";
            param.value = param.bayer_id;
          }
          if (param?.[varExtProviderID])
            param.sub_identities.push({
              type: varExtProviderID,
              value: param?.[varExtProviderID],
            });
        }
        const collectRes = await postCollect(
          { ...param },
          {},
          privacyDetails,
          isConsent,
          passEmpty,
          senderNameConfig,
        );
        return collectRes;
      } else throw error;
    }
  };

  const flattenArrays = (arr) =>
    arr.reduce((acc, item) => {
      if (Array.isArray(item)) {
        acc.push(...flattenArrays(item));
      } else {
        acc.push(item);
      }
      return acc;
    }, []);
  const filterMessages = (arr) => {
    const stringsArray = [];
    arr.forEach((item) => {
      if (typeof item === "string") {
        stringsArray.push(item);
      } else if (item && item.message) {
        if (typeof item.message === "string") stringsArray.push(item.message);
        else if (Array.isArray(item.message)) {
          stringsArray.push(...formatSuccessResponse(item.message));
        }
      }
    });
    return stringsArray;
  };
  const formatSuccessResponse = (res) => {
    const flattenArray = flattenArrays(res);
    return filterMessages(flattenArray);
  };
  const handleCollectSuccessMessage = (response) => {
    const messages = formatSuccessResponse(response?.data);
    messages?.forEach((message) => {
      successToast(message);
    });
  };
  const handleCollectErrorMessage = (error) => {
    errorToast(error?.data?.message);
  };
  return {
    identityCollect,
    handleCollectSuccessMessage,
    handleCollectErrorMessage,
  };
};

export default useCollect;